/** @jsx jsx */
import React, { forwardRef, type ReactNode, type RefObject, useCallback, useState } from 'react';
import { css, styled, jsx } from '@compiled/react';
import Tabs, { Tab, TabList, TabPanel } from '@atlaskit/tabs';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import Tooltip, { TooltipPrimitive, type TooltipPrimitiveProps } from '@atlaskit/tooltip';
// eslint-disable-next-line import/order
import Spinner from '@atlassian/jira-common-components-spinner/src/view.tsx';

import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import { useIntl } from '@atlassian/jira-intl';
import {
	ContextualAnalyticsData,
	FireScreenAnalytics,
	MODAL,
} from '@atlassian/jira-product-analytics-bridge';
import { GenericError as ErrorView } from '@atlassian/jira-servicedesk-spa-commons/src/common/utils/errors/index.tsx';
import type { OtherProjectCalendarModalMainProps } from '../../common/utils/reusable-calendars/types';
import { getOtherProjectCalendarsStateWithCanCreate } from '../../common/utils/reusable-calendars/utils';
// eslint-disable-next-line import/order
import { withFormContextProvider } from '../../controllers/form/index.tsx';

// eslint-disable-next-line import/order
import { CalendarModalMain } from '../calendars';

// eslint-disable-next-line import/order
import { CalendarModalDialog } from '../calendars/modal';

import { useCalendarsModalStateAndActions } from '../calendars/state/main.tsx';
import { OtherProjectCalendarList } from './main';
import messages from './messages';
import { useCalendarsModalStateAndActionsOtherCalendars } from './state';
import type { CalendarModalViewWithTwoTabsProps } from './types';

const ReusableSlaCalendarsStyles = css({
	minWidth: '100%',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	marginTop: `${gridSize * 3.5}px`,
});

const Panel = ({ children, testId }: { children: ReactNode; testId?: string }) => (
	<div css={ReusableSlaCalendarsStyles} data-testid={testId}>
		{children}
	</div>
);

export const OtherProjectCalendarModalMain = ({
	calendarsState,
	calendarsActions,
}: OtherProjectCalendarModalMainProps) => {
	const {
		isLoading,
		isError,
		permissionsWithTimezoneInfo,
		calendars,
		selectedCalendarMode,
		selectedCalendarId,
	} = calendarsState;

	if (isError) {
		return <ErrorView />;
	}

	if (isLoading || !permissionsWithTimezoneInfo || !calendars) {
		return <Spinner />;
	}

	return (
		<div data-test-id="servicedesk-sla-settings.ui.reusable-calendars.calendar-modal">
			<OtherProjectCalendarList
				selectedCalendarId={selectedCalendarId}
				calendars={calendars}
				permissionsWithTimezone={permissionsWithTimezoneInfo}
				selectedCalendarMode={selectedCalendarMode}
				calendarsActions={calendarsActions}
			/>
		</div>
	);
};

const CalendarModalWithTwoTabsView = ({
	isCalendarModalOpen,
	onToggleCalendarModal,
}: CalendarModalViewWithTwoTabsProps) => {
	const { formatMessage } = useIntl();
	const [calendarsState, calendarsActions] = useCalendarsModalStateAndActions(
		isCalendarModalOpen,
		onToggleCalendarModal,
	);

	const { onCreateNewRequested, onCopyCalendar } = calendarsActions;
	const { permissionsWithTimezoneInfo } = calendarsState;

	const [selectedTab, setSelectedTab] = useState<number>(0);
	const handleUpdate = useCallback((index: number) => setSelectedTab(index), [setSelectedTab]);
	const isOtherCalendarsTabSelected = (): boolean => selectedTab === 1;

	const onSelectThisCalendarTab = useCallback(() => {
		handleUpdate(0);
	}, [handleUpdate]);

	const [otherCalendarsState, otherCalendarsActions] =
		useCalendarsModalStateAndActionsOtherCalendars(
			isCalendarModalOpen,
			isOtherCalendarsTabSelected,
		);

	const onAddNewCalendar = useCallback(() => {
		onSelectThisCalendarTab(); // This will take you to the first tab since new calendars can only be added to the current project
		onCreateNewRequested();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [onSelectThisCalendarTab]);

	return (
		<CalendarModalDialog
			canCreate={Boolean(permissionsWithTimezoneInfo?.canCreate)}
			onAddCalendar={onAddNewCalendar}
			isOpen={isCalendarModalOpen}
			// @ts-expect-error - TS2769 - No overload matches this call.
			onClose={calendarsActions.onToggleCalendarModalWithNavBlocker}
		>
			<Tabs id="reusable-sla-calendars" selected={selectedTab} onChange={handleUpdate}>
				<TabList>
					<Tab>{formatMessage(messages.thisServiceProject)}</Tab>
					<Tooltip
						component={ReusableSlaCalendarTooltipStylesForTabs}
						position="right"
						content={formatMessage(messages.otherCalendarsTabTooltip)}
						truncate
					>
						<Tab>{formatMessage(messages.otherCalendarsTabTitle)}</Tab>
					</Tooltip>
				</TabList>
				<TabPanel>
					<Panel>
						<ContextualAnalyticsData
							sourceType={MODAL}
							sourceName="jsdSlaSettingsCalendarThisProjectTab"
						>
							<FireScreenAnalytics />
						</ContextualAnalyticsData>
						<CalendarModalMain
							calendarsState={calendarsState}
							calendarsActions={calendarsActions}
						/>
					</Panel>
				</TabPanel>
				<TabPanel>
					<Panel>
						<ContextualAnalyticsData
							sourceType={MODAL}
							sourceName="jsdSlaSettingsCalendarOtherCalendarsTab"
						>
							<FireScreenAnalytics />
						</ContextualAnalyticsData>
						<OtherProjectCalendarModalMain
							calendarsState={getOtherProjectCalendarsStateWithCanCreate(
								otherCalendarsState,
								Boolean(permissionsWithTimezoneInfo?.canCreate),
							)}
							calendarsActions={{
								...otherCalendarsActions,
								onSelectThisCalendarTab,
								onCopyCalendar,
							}}
						/>
					</Panel>
				</TabPanel>
			</Tabs>
		</CalendarModalDialog>
	);
};

export const CalendarModalWithTwoTabs = withFormContextProvider(CalendarModalWithTwoTabsView);

export const ReusableSlaCalendarTooltipStylesForTabs = forwardRef<
	HTMLElement,
	TooltipPrimitiveProps
>((props, ref) => (
	<ReusableSlaCalendarTooltipStylesForTabsStyle
		{...props}
		// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
		ref={ref as RefObject<HTMLDivElement>}
	/>
));

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const ReusableSlaCalendarTooltipStylesForTabsStyle = styled(TooltipPrimitive)({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	background: token('color.background.neutral.bold.hovered', colors.DN0),
	borderRadius: token('space.050', '4px'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	color: token('color.text.inverse', colors.N0),
	boxSizing: 'content-box',
	paddingLeft: token('space.100', '8px'),
	paddingRight: token('space.100', '1000px'),
	paddingTop: token('space.025', '100px'),
	height: token('space.300', '24px'),
});
